import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class Input extends i18nMixin(NeptuneElement) {

    static INPUT_TYPES = ['text', 'textarea', 'password', 'email', 'number', 'search', 'tel', 'url', 'date', 'month', 'week', 'time'];
    static MIN_MAX_STEP_TYPES = ['number', 'date', 'month', 'week', 'time'];
    static MAX_LENGTH_TYPES = ['text', 'textarea', 'password', 'email', 'search', 'tel', 'url'];
    static PATTERN_TYPES = ['text', 'search', 'url', 'tel', 'email', 'password'];
    static PLACEHOLDER_TYPES = ['text', 'search', 'url', 'tel', 'email', 'password', 'number'];
    static DEFAULT_TEL_PATTERN = '[+]?[(]?[0-9]{1,4}[)]?[0-9\\s.-]{6,19}';
    static DEFAULT_URL_PATTERN = 'https?:\\/\\/[^\\s]+';
    static TEL_VALUE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[0-9\s.-]{6,19}$/;
    static URL_HTTP_REGEX = /^https?:\/\/[^\s]+$/i;

    static INPUT_TEMPLATE = `
        <div>
            <label></label>
            <div>
                <span data-role="start-icon" hidden aria-hidden="true"><i class="fa-solid"></i></span>
                <input />
                <button type="button" data-role="end-icon-button" hidden tabindex="-1" aria-hidden="true"><i class="fa-solid"></i></button>
                <div data-role="number-stepper" hidden aria-hidden="true">
                    <button type="button" data-role="stepper-up" tabindex="-1" aria-label="Increment value"><i class="fa-solid fa-chevron-up"></i></button>
                    <button type="button" data-role="stepper-down" tabindex="-1" aria-label="Decrement value"><i class="fa-solid fa-chevron-down"></i></button>
                </div>
            </div>
            <div>
                <span data-role="supporting-text"></span>
                <ul class="neptune-list-custom"></ul>
            </div>
        </div>
    `;
    static TEXTAREA_TEMPLATE = `
        <div>
            <label></label>
            <textarea></textarea>
            <div>
                <span data-role="supporting-text"></span>
                <ul></ul>
            </div>
        </div>
    `;

    componentCssUrl = '/src/components/form/input/input.css';

    static formAssociated = true;

    #internals = null;

    constructor() {
        super();
        try {
            this.#internals = this.attachInternals?.() ?? null;
        } catch {
            this.#internals = null;
        }
    }

    #userErrorText = '';
    #internalErrorTextUpdate = false;
    #onInput = null;
    #onChange = null;
    #onBlur = null;
    #onIconAction = null;
    #onStepperUp = null;
    #onStepperDown = null;
    #connectInProgress = null;

    get value() {
        const input = this.inputElement || this.shadowRoot?.querySelector('input, textarea');
        if (!this.inputElement && input) {
            this.inputElement = input;
        }
        return this.getAttribute('value') ?? input?.value ?? '';
    }

    set value(next) {
        const normalized = next === null ? '' : String(next);
        const input = this.inputElement || this.shadowRoot?.querySelector('input, textarea');
        if (!this.inputElement && input) {
            this.inputElement = input;
        }
        if (input && input.value !== normalized) {
            input.value = normalized;
        }
        if (this.getAttribute('value') !== normalized) {
            this.setAttribute('value', normalized);
        }
        this.#internals?.setFormValue?.(normalized || null);
    }

    async connectedCallback() {
        if (this.isRendered) {
            return;
        }

        if (this.#connectInProgress) {
            await this.#connectInProgress;
            return;
        }

        const type = this.getAttribute('type') || 'text';
        if (type === 'textarea') {
            this.templateMarkup = Input.TEXTAREA_TEMPLATE;
        } else {
            this.templateMarkup = Input.INPUT_TEMPLATE;
        }

        this.#connectInProgress = (async () => {
            await super.connectedCallback();

            this.validateRequiredProperties(['type']);
            this.inputElement = this.shadowRoot.querySelector('input, textarea');

            this.#userErrorText = (this.getAttribute('error-text') || '').trim();

            const input = this.inputElement;
            if (input) {
                if (input.tagName.toLowerCase() === 'input') {
                    input.setAttribute('type', type);
                }
                Input.observedAttributes.forEach(attr => {
                    this.processAttribute(attr, null, this.getAttribute(attr));
                });
                this.handleAccessibilityAttributes(input);
            }

            this.updateTypeIcons();

            this.processInitialAttributesAfterRender(Input.observedAttributes);
        })();

        try {
            await this.#connectInProgress;
        } finally {
            this.#connectInProgress = null;
        }
    }

    setEventListeners() {
        if (this.#onInput || this.#onChange || this.#onBlur) {
            return;
        }

        const input = this.inputElement || this.shadowRoot?.querySelector('input, textarea');
        if (!this.inputElement && input) {
            this.inputElement = input;
        }
        if (!input) {
            return;
        }

        this.#onInput = () => {
            const type = this.getAttribute('type') || 'text';
            if (type === 'number' && this.inputElement?.validity?.badInput) {
                return;
            }
            const val = this.inputElement?.value ?? '';
            if (this.getAttribute('value') !== val) {
                this.setAttribute('value', val);
            }
        };

        this.#onChange = () => {
            const val = this.inputElement?.value ?? '';
            if (this.getAttribute('value') !== val) {
                this.setAttribute('value', val);
            }
            this.dispatchEvent(new CustomEvent('change', {
                detail: { value: val },
                bubbles: true,
                composed: true
            }));
        };

        this.#onBlur = () => this.validateInput();

        this.#onIconAction = (event) => {
            event.preventDefault();
            this.handleEndIconAction();
        };

        this.#onStepperUp = (event) => {
            event.preventDefault();
            this.handleNumberStep(1);
        };

        this.#onStepperDown = (event) => {
            event.preventDefault();
            this.handleNumberStep(-1);
        };

        input.addEventListener('input', this.#onInput);
        input.addEventListener('change', this.#onChange);
        input.addEventListener('blur', this.#onBlur);

        const endIconButton = this.shadowRoot?.querySelector('[data-role="end-icon-button"]');
        endIconButton?.addEventListener('click', this.#onIconAction);

        const stepUpButton = this.shadowRoot?.querySelector('[data-role="stepper-up"]');
        const stepDownButton = this.shadowRoot?.querySelector('[data-role="stepper-down"]');
        stepUpButton?.addEventListener('click', this.#onStepperUp);
        stepDownButton?.addEventListener('click', this.#onStepperDown);
    }

    removeEventListeners() {
        const input = this.inputElement;
        if (!input) {
            return;
        }
        if (this.#onInput) {
            input.removeEventListener('input', this.#onInput);
        }
        if (this.#onChange) {
            input.removeEventListener('change', this.#onChange);
        }
        if (this.#onBlur) {
            input.removeEventListener('blur', this.#onBlur);
        }

        const endIconButton = this.shadowRoot?.querySelector('[data-role="end-icon-button"]');
        if (endIconButton && this.#onIconAction) {
            endIconButton.removeEventListener('click', this.#onIconAction);
        }

        const stepUpButton = this.shadowRoot?.querySelector('[data-role="stepper-up"]');
        const stepDownButton = this.shadowRoot?.querySelector('[data-role="stepper-down"]');
        if (stepUpButton && this.#onStepperUp) {
            stepUpButton.removeEventListener('click', this.#onStepperUp);
        }
        if (stepDownButton && this.#onStepperDown) {
            stepDownButton.removeEventListener('click', this.#onStepperDown);
        }

        this.#onInput = null;
        this.#onChange = null;
        this.#onBlur = null;
        this.#onIconAction = null;
        this.#onStepperUp = null;
        this.#onStepperDown = null;
    }

    static get observedAttributes() {
        return ['type', 'label', 'placeholder', 'value', 'disabled', 'error', 'required', 'supporting-text', 'min', 'max', 'step', 'readonly', 'maxlength', 'minlength', 'pattern', 'autocomplete', 'error-text', 'name', 'culture'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        if (name === 'culture') {
            if (newValue) {
                i18n.setLocale(newValue);
                this.onLocaleChange();
            }
            return;
        }

        const input = this.inputElement || this.shadowRoot?.querySelector('input, textarea');
        if (!this.inputElement && input) {
            this.inputElement = input;
        }
        if (!input) {return;}

        const type = this.getAttribute('type') || 'text';

        switch (name) {
            case 'type': {
                if (input.tagName.toLowerCase() === 'input') {
                    const nextType = newValue && newValue !== 'textarea' ? newValue : 'text';
                    input.setAttribute('type', nextType);
                }
                this.handleMinMaxStepAttributes(input, type);
                this.handleLengthAttributes(input, type);
                this.handlePatternAttribute(input, type);
                this.handleAutocompleteAttribute(input);
                this.updateTypeIcons();
                break;
            }
            case 'placeholder': {
                if (Input.PLACEHOLDER_TYPES.includes(type) && newValue !== null) {
                    input.setAttribute('placeholder', newValue);
                } else {
                    input.removeAttribute('placeholder');
                }
                break;
            }
            case 'value': {
                input.value = newValue || '';
                if (input.hasAttribute('pattern') && newValue !== null && newValue !== '') {
                    this.validateInput();
                }
                this.#internals?.setFormValue?.(newValue || null);
                this.updateTypeIcons();
                break;
            }
            case 'disabled': {
                this.hasAttribute('disabled')
                    ? input.setAttribute('disabled', '')
                    : input.removeAttribute('disabled');
                this.updateTypeIcons();
                break;
            }
            case 'name': {
                newValue !== null
                    ? input.setAttribute('name', newValue)
                    : input.removeAttribute('name');
                break;
            }
            case 'required': {
                this.hasAttribute('required')
                    ? input.setAttribute('required', '')
                    : input.removeAttribute('required');
                break;
            }
            case 'readonly': {
                this.hasAttribute('readonly')
                    ? input.setAttribute('readonly', '')
                    : input.removeAttribute('readonly');
                this.updateTypeIcons();
                break;
            }
            case 'error': {
                this.hasAttribute('error')
                    ? input.setAttribute('aria-invalid', 'true')
                    : input.removeAttribute('aria-invalid');
                this.updateMessages();
                this.updateTypeIcons();
                break;
            }
            case 'min': {
                this.handleMinMaxStepAttributes(input, type);
                this.updateTypeIcons();
                break;
            }
            case 'max': {
                this.handleMinMaxStepAttributes(input, type);
                this.updateTypeIcons();
                break;
            }
            case 'step': {
                this.handleMinMaxStepAttributes(input, type);
                this.updateTypeIcons();
                break;
            }
            case 'maxlength': {
                this.handleLengthAttributes(input, type);
                break;
            }
            case 'minlength': {
                this.handleLengthAttributes(input, type);
                break;
            }
            case 'pattern': {
                this.handlePatternAttribute(input, type);
                break;
            }
            case 'autocomplete': {
                this.handleAutocompleteAttribute(input);
                break;
            }
            case 'label': {
                this.setLabel();
                break;
            }
            case 'supporting-text':
            case 'error-text': {
                if (name === 'error-text' && !this.#internalErrorTextUpdate) {
                    this.#userErrorText = (newValue || '').trim();
                }
                this.updateMessages();
                break;
            }
            default: {
                break;
            }
        }
    }

    onLocaleChange() {
        if (this.inputElement) {
            this.validateInput();
        }
    }

    validateInput() {
        const input = this.inputElement;
        if (!input) {return;}

        const label = this.getAttribute('label') || 'Input';
        const type = this.getAttribute('type') || 'text';
        const inputValue = (input.value || '').trim();
        const errors = new Set();

        if (input.validity.valueMissing) {
            errors.add(`${label} ${this.translate('INPUT.VALIDATION.REQUIRED')}`);
        }

        const mismatchMsg = this.#userErrorText || `${this.translate('INPUT.VALIDATION.INVALID')} ${label}`;

        if (input.validity.typeMismatch || input.validity.patternMismatch || input.validity.badInput) {
            errors.add(mismatchMsg);
        }

        if (input.validity.tooShort) {
            const minLength = this.getAttribute('minlength');
            errors.add(`${label} ${this.translate('INPUT.VALIDATION.TOO_SHORT')} ${minLength} ${this.translate('INPUT.VALIDATION.CHARACTERS')}`);
        }

        if (input.validity.tooLong) {
            const maxLength = this.getAttribute('maxlength');
            errors.add(`${label} ${this.translate('INPUT.VALIDATION.TOO_LONG')} ${maxLength} ${this.translate('INPUT.VALIDATION.CHARACTERS')}`);
        }

        if (input.validity.tooShort || input.validity.tooLong) {
            const minLength = this.getAttribute('minlength');
            const maxLength = this.getAttribute('maxlength');
            if (minLength && maxLength) {
                errors.add(`${this.translate('INPUT.VALIDATION.BETWEEN_RANGE')} ${minLength}-${maxLength} ${this.translate('INPUT.VALIDATION.CHARACTERS')}`);
            }
        }

        if (input.validity.rangeUnderflow || input.validity.rangeOverflow) {
            const min = this.getAttribute('min');
            const max = this.getAttribute('max');
            if (min && max) {
                errors.add(`${this.translate('INPUT.VALIDATION.BETWEEN_RANGE')} ${min} and ${max}`);
            } else if (input.validity.rangeUnderflow) {
                errors.add(`${this.translate('INPUT.VALIDATION.AT_LEAST')} ${min}`);
            } else if (input.validity.rangeOverflow) {
                errors.add(`${this.translate('INPUT.VALIDATION.CANNOT_EXCEED')} ${max}`);
            }
        }

        if (input.validity.stepMismatch) {
            const step = this.getAttribute('step');
            const min = this.getAttribute('min') || 0;
            const max = this.getAttribute('max');
            if (step && max) {
                errors.add(`${this.translate('INPUT.VALIDATION.INCREMENTS_RANGE')} ${step} between ${min} and ${max}`);
            } else if (step) {
                errors.add(`${this.translate('INPUT.VALIDATION.INCREMENTS')} ${step}`);
            }
        }

        if (type === 'url' && inputValue && !Input.URL_HTTP_REGEX.test(inputValue)) {
            errors.add(this.#userErrorText || `${this.translate('INPUT.VALIDATION.INVALID')} ${label}`);
        }

        const hasCustomPattern = !!(this.getAttribute('pattern') && this.getAttribute('pattern').trim());
        if (type === 'tel' && !hasCustomPattern && inputValue && !Input.TEL_VALUE_REGEX.test(inputValue)) {
            errors.add(this.#userErrorText || `${this.translate('INPUT.VALIDATION.INVALID')} ${label}`);
        }

        const errorText = [...errors].join('|');

        this.#internalErrorTextUpdate = true;
        try {
            if (errorText) {
                this.setAttribute('error', '');
                this.setAttribute('error-text', errorText);
            } else {
                this.removeAttribute('error');
                this.removeAttribute('error-text');
            }
        } finally {
            this.#internalErrorTextUpdate = false;
        }

        this.updateMessages();

        if (this.#internals) {
            if (errorText) {
                this.#internals.setValidity?.({ customError: true }, errorText.split('|')[0], input);
            } else {
                this.#internals.setValidity?.({});
            }
        }
    }

    formResetCallback() {
        this.setAttribute('value', '');
        this.removeAttribute('error');
        this.removeAttribute('error-text');
    }

    formDisabledCallback(isDisabled) {
        if (isDisabled) {
            this.setAttribute('disabled', '');
        } else {
            this.removeAttribute('disabled');
        }
    }

    get validity() {
        return this.#internals?.validity;
    }

    get validationMessage() {
        return this.#internals?.validationMessage ?? '';
    }

    get willValidate() {
        return this.#internals?.willValidate ?? false;
    }

    checkValidity() {
        return this.#internals?.checkValidity?.() ?? true;
    }

    reportValidity() {
        return this.#internals?.reportValidity?.() ?? true;
    }

    handleLengthAttributes(input, type) {
        if (!Input.MAX_LENGTH_TYPES.includes(type)) {
            input.removeAttribute('maxlength');
            input.removeAttribute('minlength');
            return;
        }
        const maxLength = this.getAttribute('maxlength');
        const minLength = this.getAttribute('minlength');
        if (maxLength !== null && minLength !== null && parseInt(maxLength) < parseInt(minLength)) {
            throw new Error(`The maxlength attribute value (${maxLength}) cannot be less than minlength attribute value (${minLength}).`);
        }
        maxLength !== null ? input.setAttribute('maxlength', maxLength) : input.removeAttribute('maxlength');
        minLength !== null ? input.setAttribute('minlength', minLength) : input.removeAttribute('minlength');
    }

    handleMinMaxStepAttributes(input, type) {
        if (!Input.MIN_MAX_STEP_TYPES.includes(type)) {
            input.removeAttribute('min');
            input.removeAttribute('max');
            input.removeAttribute('step');
            return;
        }
        const min = this.getAttribute('min');
        const max = this.getAttribute('max');
        const step = this.getAttribute('step');
        min !== null ? input.setAttribute('min', min) : input.removeAttribute('min');
        max !== null ? input.setAttribute('max', max) : input.removeAttribute('max');
        step !== null ? input.setAttribute('step', step) : input.removeAttribute('step');
    }

    handlePatternAttribute(input, type) {
        if (!Input.PATTERN_TYPES.includes(type)) {
            input.removeAttribute('pattern');
            return;
        }
        const pattern = this.getAttribute('pattern');
        const hasUserPattern = pattern && pattern.trim() !== '';

        if (hasUserPattern) {
            const errorText = this.getAttribute('error-text');
            if (!errorText || !errorText.trim()) {
                const label = this.getAttribute('label') || 'Input';
                const fallbackErrorText = `${this.translate('INPUT.VALIDATION.INVALID')} ${label}`;
                this.setAttribute('error-text', fallbackErrorText);
            }
            input.setAttribute('pattern', pattern.trim());
            return;
        }

        const defaultPattern = this.getDefaultPattern(type);
        if (defaultPattern) {
            input.setAttribute('pattern', defaultPattern);
            return;
        }

        input.removeAttribute('pattern');
    }

    getDefaultPattern(type) {
        if (type === 'url') {
            return Input.DEFAULT_URL_PATTERN;
        }

        if (type === 'tel') {
            return Input.DEFAULT_TEL_PATTERN;
        }

        return '';
    }

    handleAutocompleteAttribute(input) {
        const autocomplete = this.getAttribute('autocomplete');
        autocomplete !== null ? input.setAttribute('autocomplete', autocomplete) : input.setAttribute('autocomplete', 'off');
    }

    handleAccessibilityAttributes(input) {
        const inputId = `neptune-input-${Math.random().toString(36).substr(2, 9)}`;
        input.id = inputId;

        const label = this.shadowRoot.querySelector('label');
        if (label) {label.setAttribute('for', inputId);}

        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('[data-role="supporting-text"]');

        if (errorList) {
            errorList.id = `neptune-input-errors-${Math.random().toString(36).substr(2, 9)}`;
        }

        if (supportingText) {
            supportingText.id = `neptune-input-support-${Math.random().toString(36).substr(2, 9)}`;
        }

        const describedBy = [errorList?.id, supportingText?.id].filter(Boolean).join(' ');
        if (describedBy) {
            input.setAttribute('aria-describedby', describedBy);
        }
    }

    setLabel() {
        const labelElement = this.shadowRoot.querySelector('label');
        if (!labelElement) {
            return;
        }
        const labelText = this.getAttribute('label') || '';
        labelElement.textContent = labelText;
    }

    setSupportingText() {
        const supportingText = this.shadowRoot.querySelector('[data-role="supporting-text"]');
        if (!supportingText) {
            return;
        }
        const supportingTextValue = this.getAttribute('supporting-text') || '';
        supportingText.textContent = supportingTextValue;
    }

    updateMessages() {
        const errorList = this.shadowRoot.querySelector('ul');
        const supportingText = this.shadowRoot.querySelector('[data-role="supporting-text"]');

        if (!errorList || !supportingText) {return;}

        errorList.setAttribute('aria-live', 'polite');
        supportingText.setAttribute('aria-live', 'polite');

        if (this.hasAttribute('error')) {
            const errorText = this.getAttribute('error-text') || '';
            const errors = errorText.split('|').filter(e => e.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach(error => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
                errorList.style.display = 'block';
            } else {
                errorList.style.display = 'none';
            }

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            if (supportingTextValue) {
                supportingText.textContent = supportingTextValue;
                supportingText.classList.add('visually-hidden-a11y');
                supportingText.style.display = 'block';
            } else {
                supportingText.style.display = 'none';
            }
        } else {
            errorList.innerHTML = '';
            errorList.style.display = 'none';

            const supportingTextValue = this.getAttribute('supporting-text') || '';
            supportingText.textContent = supportingTextValue;
            supportingText.classList.remove('visually-hidden-a11y');
            supportingText.style.display = supportingTextValue ? 'block' : 'none';
        }
    }

    updateTypeIcons() {
        const input = this.inputElement || this.shadowRoot?.querySelector('input');
        if (!input || input.tagName.toLowerCase() !== 'input') {
            return;
        }

        const type = this.getAttribute('type') || 'text';
        const startIconWrapper = this.shadowRoot?.querySelector('[data-role="start-icon"]');
        const startIcon = startIconWrapper?.querySelector('i');
        const endIconButton = this.shadowRoot?.querySelector('[data-role="end-icon-button"]');
        const endIcon = endIconButton?.querySelector('i');
        const numberStepper = this.shadowRoot?.querySelector('[data-role="number-stepper"]');
        const stepUpButton = this.shadowRoot?.querySelector('[data-role="stepper-up"]');
        const stepDownButton = this.shadowRoot?.querySelector('[data-role="stepper-down"]');

        if (!startIconWrapper || !startIcon || !endIconButton || !endIcon || !numberStepper || !stepUpButton || !stepDownButton) {
            return;
        }

        const startIconMap = {
            search: 'fa-magnifying-glass',
            tel: 'fa-phone',
            email: 'fa-envelope'
        };

        const startIconClass = startIconMap[type] || '';
        if (startIconClass) {
            startIcon.className = `fa-solid ${startIconClass}`;
            startIconWrapper.hidden = false;
        } else {
            startIcon.className = 'fa-solid';
            startIconWrapper.hidden = true;
        }

        const hasError = this.hasAttribute('error');

        let endIconClass = '';
        if (hasError && type === 'search') {
            endIconClass = 'fa-circle-exclamation';
        } else if (type === 'date' || type === 'month' || type === 'week') {
            endIconClass = 'fa-calendar';
        } else if (type === 'password') {
            endIconClass = input.getAttribute('type') === 'text' ? 'fa-eye-slash' : 'fa-eye';
        } else if (type === 'search' && input.value) {
            endIconClass = 'fa-circle-xmark';
        }

        if (endIconClass) {
            endIcon.className = `fa-solid ${endIconClass}`;
            endIconButton.hidden = false;
        } else {
            endIcon.className = 'fa-solid';
            endIconButton.hidden = true;
        }

        if (type === 'number') {
            numberStepper.hidden = false;
            this.updateNumberStepperState(stepUpButton, stepDownButton);
        } else {
            numberStepper.hidden = true;
            stepUpButton.disabled = true;
            stepDownButton.disabled = true;
        }

        const canInteract = ['password', 'date', 'month', 'week'].includes(type) || (type === 'search' && !hasError);
        const isDisabled = this.hasAttribute('disabled');
        const isReadonly = this.hasAttribute('readonly');
        endIconButton.disabled = isDisabled || !canInteract || ((type === 'date' || type === 'month' || type === 'week') && isReadonly);

        if (type === 'password') {
            endIconButton.setAttribute('aria-label', 'Toggle password visibility');
        } else if (type === 'search') {
            endIconButton.setAttribute('aria-label', 'Clear search value');
        } else if (type === 'date' || type === 'month' || type === 'week') {
            endIconButton.setAttribute('aria-label', 'Open calendar picker');
        } else {
            endIconButton.removeAttribute('aria-label');
        }
    }

    handleEndIconAction() {
        const input = this.inputElement || this.shadowRoot?.querySelector('input');
        if (!input || input.tagName.toLowerCase() !== 'input' || this.hasAttribute('disabled')) {
            return;
        }

        const type = this.getAttribute('type') || 'text';

        if (this.hasAttribute('error') && type === 'search') {
            return;
        }

        if (type === 'password') {
            const nextType = input.getAttribute('type') === 'text' ? 'password' : 'text';
            input.setAttribute('type', nextType);
            this.updateTypeIcons();
            return;
        }

        if (type === 'search') {
            if (!input.value) {
                return;
            }
            input.value = '';
            input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
            input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
            input.focus();
            this.updateTypeIcons();
            return;
        }

        if (type === 'date' || type === 'month' || type === 'week') {
            if (this.hasAttribute('readonly')) {
                return;
            }
            if (typeof input.showPicker === 'function') {
                input.showPicker();
            } else {
                input.focus();
                input.click();
            }
        }
    }

    updateNumberStepperState(stepUpButton, stepDownButton) {
        const input = this.inputElement || this.shadowRoot?.querySelector('input');
        if (!input || input.tagName.toLowerCase() !== 'input') {
            return;
        }

        const isDisabled = this.hasAttribute('disabled');
        const isReadonly = this.hasAttribute('readonly');
        if (isDisabled || isReadonly) {
            stepUpButton.disabled = true;
            stepDownButton.disabled = true;
            return;
        }

        const min = this.parseNumericAttribute('min');
        const max = this.parseNumericAttribute('max');
        const current = this.parseNumericValue(input.value);

        if (current === null) {
            stepUpButton.disabled = false;
            stepDownButton.disabled = false;
            return;
        }

        stepUpButton.disabled = max !== null && current >= max;
        stepDownButton.disabled = min !== null && current <= min;
    }

    parseNumericAttribute(name) {
        const rawValue = this.getAttribute(name);
        if (rawValue === null || rawValue === '') {
            return null;
        }
        const parsed = Number(rawValue);
        return Number.isFinite(parsed) ? parsed : null;
    }

    parseNumericValue(value) {
        if (value === null || value === undefined || String(value).trim() === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }

    normalizeStepValue() {
        const stepAttr = this.getAttribute('step');
        if (!stepAttr || stepAttr === 'any') {
            return 1;
        }

        const parsed = Number(stepAttr);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return 1;
        }

        return parsed;
    }

    formatNumericValue(value) {
        if (Number.isInteger(value)) {
            return String(value);
        }

        return String(Number(value.toFixed(12)));
    }

    computeNextSteppedValue(direction) {
        const input = this.inputElement || this.shadowRoot?.querySelector('input');
        if (!input || input.tagName.toLowerCase() !== 'input') {
            return null;
        }

        const step = this.normalizeStepValue();
        const min = this.parseNumericAttribute('min');
        const max = this.parseNumericAttribute('max');
        const current = this.parseNumericValue(input.value);
        const stepBase = min ?? 0;
        const epsilon = step / 1000000;

        let next = null;

        if (current === null) {
            if (direction > 0) {
                next = min ?? 0;
            } else if (max !== null && min !== null) {
                const stepsInRange = Math.floor(((max - min) / step) + epsilon);
                next = min + (stepsInRange * step);
            } else if (max !== null) {
                next = max;
            } else {
                next = (min ?? 0) - step;
            }
        } else {
            const rawUnits = (current - stepBase) / step;
            const roundedUnits = Math.round(rawUnits);
            const isAligned = Math.abs(rawUnits - roundedUnits) <= epsilon;

            if (direction > 0) {
                const nextUnits = isAligned ? roundedUnits + 1 : Math.ceil(rawUnits - epsilon);
                next = stepBase + (nextUnits * step);
            } else {
                const nextUnits = isAligned ? roundedUnits - 1 : Math.floor(rawUnits + epsilon);
                next = stepBase + (nextUnits * step);
            }
        }

        if (next === null || !Number.isFinite(next)) {
            return null;
        }

        if (min !== null && next < min - epsilon) {
            return null;
        }

        if (max !== null && next > max + epsilon) {
            return null;
        }

        if (min !== null && next < min) {
            next = min;
        }

        if (max !== null && next > max) {
            next = max;
        }

        return this.formatNumericValue(next);
    }

    handleNumberStep(direction) {
        const input = this.inputElement || this.shadowRoot?.querySelector('input');
        if (!input || input.tagName.toLowerCase() !== 'input') {
            return;
        }

        if ((this.getAttribute('type') || 'text') !== 'number') {
            return;
        }

        if (this.hasAttribute('disabled') || this.hasAttribute('readonly')) {
            return;
        }

        const nextValue = this.computeNextSteppedValue(direction);
        if (nextValue === null || nextValue === input.value) {
            this.updateTypeIcons();
            return;
        }

        input.value = nextValue;
        input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        input.focus();
        this.updateTypeIcons();
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }
}

customElements.define('neptune-input', Input);

export default Input;